import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Input, Switch, Radio, RadioGroup, Select, SelectItem } from '@nextui-org/react';
import {
  ArrowLeft, Trophy, Plus, Calendar, MapPin, Share2, Eye, X,
} from 'lucide-react';
import tournamentService from '../services/tournamentService';

const TEAL = '#3FCED2';

const FEATURES = [
  {
    icon: <Trophy className="w-6 h-6" />,
    title: 'TURNIER-PROFIL',
    text: 'Erstelle ein detailliertes Profil mit allen Infos rund um dein Turnier.',
  },
  {
    icon: <Eye className="w-6 h-6" />,
    title: 'SICHTBARKEIT',
    text: 'Erreiche Dart-Spieler aus deiner Region und darüber hinaus.',
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: 'STANDORT & ADRESSE',
    text: 'Über die Karten- und Standortsuche wird dein Turnier leicht gefunden.',
  },
  {
    icon: <Share2 className="w-6 h-6" />,
    title: 'ANMELDUNG & TEILNAHME',
    text: 'Nutze die DartCloud-Anmeldung oder verlinke auf ein externes System.',
  },
];

const TOURNAMENT_TYPES = [
  { value: 'single', label: 'Einzel' },
  { value: 'double', label: 'Doppel' },
  { value: 'team', label: 'Team' },
];

type RegistrationMode = 'dartcloud' | 'external' | 'none';

const CreateTournamentPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [type, setType] = useState<string>('single');
  const [steeldart, setSteeldart] = useState(false);
  const [edart, setEdart] = useState(false);
  const [online, setOnline] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [newDate, setNewDate] = useState('');
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode>('dartcloud');
  const [entryLink, setEntryLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addDate = () => {
    if (!newDate) return;
    if (dates.includes(newDate)) return;
    setDates([...dates, newDate].sort());
    setNewDate('');
  };

  const removeDate = (d: string) => setDates(dates.filter((x) => x !== d));

  const handleCreate = async () => {
    if (!name.trim()) { setError('Bitte einen Namen festlegen'); return; }
    if (name.trim().length < 3) { setError('Der Name muss mindestens 3 Zeichen lang sein'); return; }
    if (!steeldart && !edart && !online) { setError('Bitte wähle mindestens eine Kategorie aus'); return; }
    if (dates.length === 0) { setError('Bitte wähle mindestens ein Datum aus'); return; }

    setSaving(true); setError(null);
    try {
      const categories: string[] = [];
      if (steeldart) categories.push('steeldart');
      if (edart) categories.push('edart');
      if (online) categories.push('online');

      let registrationType: 'DARTCLOUD' | 'EXTERNAL' | 'NONE' = 'DARTCLOUD';
      if (registrationMode === 'external') registrationType = 'EXTERNAL';
      if (registrationMode === 'none') registrationType = 'NONE';

      const dateTimestamps = dates.map((d) => new Date(d).getTime()).filter((t) => !isNaN(t));

      const payload: any = {
        name: name.trim(),
        categories,
        type,
        dates: dateTimestamps,
        registrationType,
        entryEnabled: registrationMode === 'dartcloud',
      };

      if (registrationMode === 'external' && entryLink.trim()) {
        payload.entryLink = entryLink.trim();
      }

      const res = await tournamentService.createTournament(payload);
      const created = (res as any)?.data ?? res;
      const id = created?._id ?? created?.id;
      if (id) navigate(`/verwaltung/turnier/${id}/bearbeiten`);
      else navigate('/verwaltung?tab=tournaments');
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Turnier konnte nicht erstellt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet><title>Turnier erstellen - DartCloud</title></Helmet>

      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/verwaltung?tab=tournaments')} className="text-zinc-300 hover:text-white p-1">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold flex-1">Turnier erstellen</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        <section className="text-center py-6">
          <div className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>
            <Trophy size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-2">Dein Turnier in der DartCloud</h2>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto">
            Erstelle ein Turnier und gewinne Teilnehmer aus der Dart-Community.
          </p>
        </section>

        <section className="space-y-4">
          <Input
            label="Turniername" placeholder="z.B. Frühjahrs-Cup 2026"
            value={name} onValueChange={(v) => { setName(v); setError(null); }}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} isRequired
          />

          <Select
            label="Turniertyp" selectedKeys={[type]}
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string;
              if (v) setType(v);
            }}
            classNames={{ trigger: 'bg-zinc-900 border border-zinc-800' }}
          >
            {TOURNAMENT_TYPES.map((t) => <SelectItem key={t.value}>{t.label}</SelectItem>)}
          </Select>

          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold">Kategorien *</div>
            <p className="text-xs text-zinc-500 -mt-1">Wähle eine oder mehrere Kategorien aus</p>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Steeldart</div>
                <div className="text-xs text-zinc-500">Klassisches Dart mit Stahlspitzen</div>
              </div>
              <Switch isSelected={steeldart} onValueChange={(v) => { setSteeldart(v); setError(null); }} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">E-Dart</div>
                <div className="text-xs text-zinc-500">Elektronisches Dart</div>
              </div>
              <Switch isSelected={edart} onValueChange={(v) => { setEdart(v); setError(null); }} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Online</div>
                <div className="text-xs text-zinc-500">Online-Turnier (z.B. Webcam Darts)</div>
              </div>
              <Switch isSelected={online} onValueChange={(v) => { setOnline(v); setError(null); }} />
            </div>
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Calendar size={14} /> Turnier-Termine *
            </div>
            <p className="text-xs text-zinc-500 -mt-1">Wähle ein oder mehrere Daten aus</p>
            <div className="flex gap-2">
              <Input type="date" value={newDate} onValueChange={setNewDate}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
              <Button onPress={addDate} isDisabled={!newDate}
                style={{ backgroundColor: TEAL, color: '#000' }} startContent={<Plus size={14} />}>
                Hinzufügen
              </Button>
            </div>
            {dates.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {dates.map((d) => (
                  <div key={d} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-zinc-700 bg-zinc-900">
                    <span>{new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    <button onClick={() => removeDate(d)} className="text-zinc-400 hover:text-red-400">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold">Anmeldung *</div>
            <RadioGroup value={registrationMode} onValueChange={(v) => setRegistrationMode(v as RegistrationMode)}
              classNames={{ wrapper: 'gap-2' }}>
              <Radio value="dartcloud" description="Teilnehmer melden sich direkt über DartCloud an">DartCloud Anmeldung</Radio>
              <Radio value="external" description="Weiterleitung zu einer externen Anmeldeseite">Externer Link</Radio>
              <Radio value="none" description="Turnier ohne Online-Anmeldung">Keine Anmeldung</Radio>
            </RadioGroup>
            {registrationMode === 'external' && (
              <Input label="Externer Link" placeholder="https://..." value={entryLink} onValueChange={setEntryLink}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            )}
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-3 text-sm">{error}</div>}

          <div className="rounded-xl p-3 text-sm flex items-start gap-2"
            style={{ backgroundColor: `${TEAL}11`, color: TEAL, border: `1px solid ${TEAL}33` }}>
            <Trophy size={16} className="mt-0.5 flex-shrink-0" />
            <span className="text-zinc-300">
              Weitere Details wie Adresse, Spielmodus, Startgebühr, Uhrzeiten und Beschreibung kannst du nach dem Erstellen bearbeiten.
            </span>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-semibold">Vorteile</h3>
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>{f.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold tracking-wide mb-1">{f.title}</div>
                <p className="text-sm text-zinc-400">{f.text}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button className="w-full" isDisabled={saving || !name.trim()} isLoading={saving} onPress={handleCreate}
            startContent={!saving ? <Plus size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}>Turnier erstellen</Button>
        </div>
      </div>
    </div>
  );
};

export default CreateTournamentPage;
